import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AppServices } from 'middleware'
import { MembershipListParams, MembershipListResult, MembershipDetailsParams } from './membershipsService'
import { Membership, MembershipDetailed } from './schema'
import { DeepPartial } from 'utility-types'
import { Comment } from 'uiComponents/comment/comment'
import { ArticlesWithPricingPlansResponse } from 'products/pricing/pricingPlans'

export const membershipsApi = createApi({
    reducerPath: 'membershipsApi',
    tagTypes: ['listMemberships', 'membershipDetails', 'membershipPayments'],
    baseQuery: fetchBaseQuery({}),
    endpoints: (builder) => ({}),
})

const extendedMembershipsApi = membershipsApi.injectEndpoints({
    endpoints: (builder) => ({
        listMemberships: builder.query<MembershipListResult, MembershipListParams>({
            providesTags: ['listMemberships'],
            queryFn: async (params: MembershipListParams) => {
                const data = await AppServices.membershipsService.listMemberships(params)
                return { data }
            },
        }),
        getMembershipDetails: builder.query<MembershipDetailed, MembershipDetailsParams>({
            providesTags: (result, error, { uuid }) => [
                { type: 'membershipDetails', id: uuid },
                { type: 'membershipPayments', id: uuid },
            ],
            queryFn: async ({ accountSlug, uuid }) => {
                const data = await AppServices.membershipsService.getMembershipDetails({
                    accountSlug,
                    uuid,
                })
                return { data }
            },
        }),
        updateMembership: builder.mutation({
            queryFn: async ({
                accountSlug,
                uuid,
                overrides,
            }: {
                accountSlug: string
                uuid: string
                overrides: DeepPartial<Membership> | { comment: Comment }
            }) => {
                const response = await AppServices.membershipsService.updateMembership({
                    accountSlug,
                    uuid,
                    overrides,
                })
                return { data: response }
            },
            async onQueryStarted({ accountSlug, uuid }, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled
                dispatch(
                    extendedMembershipsApi.util.updateQueryData(
                        'getMembershipDetails',
                        { accountSlug, uuid },
                        () => data,
                    ),
                )
            },
        }),
        cancelMembership: builder.mutation({
            invalidatesTags: (result, error, { uuid }) => [{ type: 'membershipDetails', id: uuid }],
            queryFn: async ({ accountSlug, uuid }) => {
                const response = await AppServices.membershipsService.cancelMembership({ accountSlug, uuid })
                return { data: response }
            },
        }),
        disableAutoRenewal: builder.mutation({
            invalidatesTags: (result, error, { uuid }) => [{ type: 'membershipDetails', id: uuid }],
            queryFn: async ({ accountSlug, uuid }) => {
                const response = await AppServices.membershipsService.disableAutoRenewal({ accountSlug, uuid })
                return { data: response }
            },
        }),
        refundMembershipPayment: builder.mutation({
            invalidatesTags: (result, error, { uuid }) => [{ type: 'membershipPayments', id: uuid }],
            queryFn: async ({ accountSlug, uuid, paymentUuid }) => {
                const response = await AppServices.membershipsService.refundMembershipPayment({
                    accountSlug,
                    uuid,
                    paymentUuid,
                })
                return { data: response }
            },
        }),
        getArticlesWithPricingPlans: builder.query<ArticlesWithPricingPlansResponse, string>({
            queryFn: async (accountSlug: string) => {
                const response = await AppServices.pricingPlansService.getArticlesWithPricingPlans(accountSlug)
                return { data: response }
            },
        }),
    }),
})

export const { reducer, reducerPath } = membershipsApi
export const {
    useListMembershipsQuery,
    useLazyListMembershipsQuery,
    useGetMembershipDetailsQuery,
    useLazyGetMembershipDetailsQuery,
    useUpdateMembershipMutation,
    useCancelMembershipMutation,
    useDisableAutoRenewalMutation,
    useRefundMembershipPaymentMutation,
    useGetArticlesWithPricingPlansQuery,
} = extendedMembershipsApi

export const selectListMembershipsResult = extendedMembershipsApi.endpoints.listMemberships.select
export const endpoints = extendedMembershipsApi.endpoints
